import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { useMemo } from 'react'
import { useQuery } from 'react-query'
import type { Resource } from '../../types'
import type { ShopIdToIsCabinOnly } from '../../util/filterSlotsByResourceTypes'
import { buildShopIdToIsCabinOnly } from '../../util/buildShopIdToIsCabinOnly'
import { getDeliveryModelFromJourney } from '../../util/getDeliveryModelFromJourney'
import { useAppointmentsService } from '../services'
import { useFeatureFlags } from '../useFeatureFlags'

const hookName = 'useShopIdToIsCabinOnly'

export interface UseShopIdToIsCabinOnlyParams {
  journey: string | undefined
  resources: Resource[]
  isEnabled?: boolean
}

/**
 * Fetches shops by resource shop ids and returns a map of shop id → CABIN_ONLY for the journey's delivery model.
 * Only runs when ENABLE_SHOP_DELIVERY_MODEL_CONFIGURATION is on and delivery model is NETWORK.
 * Used by schedule and reservation forms to pass shopIdToIsCabinOnly into useCombinedResourceAvailabilities.
 */
const useShopIdToIsCabinOnly = ({
  journey,
  resources,
  isEnabled = true,
}: UseShopIdToIsCabinOnlyParams): {
  shopIdToIsCabinOnly: ShopIdToIsCabinOnly | undefined
  isLoading: boolean
} => {
  const { getShops, isReady } = useAppointmentsService()
  const [isShopDeliveryModelConfigurationEnabled] = useFeatureFlags<boolean>(['ENABLE_SHOP_DELIVERY_MODEL_CONFIGURATION'])

  const shopIds = useMemo(() => {
    const ids = new Set(resources.map((r) => r.shop_id))
    return Array.from(ids)
  }, [resources])

  const sortedShopIds = useMemo(() => [...shopIds].sort((a, b) => a.localeCompare(b)), [shopIds])

  const shouldFetch =
    Boolean(isReady && isEnabled && isShopDeliveryModelConfigurationEnabled) &&
    getDeliveryModelFromJourney(journey) === DeliveryModel.NETWORK &&
    sortedShopIds.length > 0

  const fetchShops = async () => {
    const page = await getShops({ id: sortedShopIds })
    return page.data
  }

  const { data: shops, isLoading } = useQuery([hookName, sortedShopIds.length ? sortedShopIds.join(',') : '', journey], fetchShops, {
    enabled: shouldFetch,
  })

  const shopIdToIsCabinOnly = useMemo((): ShopIdToIsCabinOnly | undefined => {
    if (!shops || shops.length === 0) return undefined
    return buildShopIdToIsCabinOnly(shops, journey)
  }, [shops, journey])

  return {
    shopIdToIsCabinOnly: shouldFetch ? shopIdToIsCabinOnly : undefined,
    isLoading: shouldFetch && isLoading,
  }
}

export { useShopIdToIsCabinOnly }
